import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
} from 'react';
import format from 'string-template';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import moment from 'moment-timezone';

import useComponentMounted from '../../../../../hooks/useComponentMounted';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import { TimeDurationFormat } from '../../../../../utils/time';
import { DateFormat } from '../../../../../utils/date';

import {
  DAY_GRID_MONTH,
  CAL_DOT_COM_TIME_ZONE,
  MAX_SEATS_PER_SLOT,
} from '../../constants';

import AvilabilityModal from '../AvilabilityModal';
import {
  generateAvailabilityEvents,
} from '../../utils';

import {
  CalendarWrapper,
  EventContent,
  EventContentError,
  EventTime,
  EventTitle,
  TimeZoneText,
} from './styles';
import texts from './texts.json';

const AvilabilityCalendar = () => {
  const [currentView, setCurrentView] = useState(DAY_GRID_MONTH);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availabilityEventDetails, setAvailabilityEventDetails] = useState();
  const [visibleDateRange, setVisibleDateRange] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        setIsLoading(true);
        if (visibleDateRange) {
          const availabilityData = await generateAvailabilityEvents(visibleDateRange);
          setAvailabilityEventDetails(availabilityData);
        }
        setIsLoading(false);
      } catch (err) {
        if (isComponentMountedRef.current) {
          setIsLoading(false);
          setError(err);
        }
      }
    };
    fetchEventDetails();
  }, [isComponentMountedRef, visibleDateRange]);

  const filteredEventsForSelectedDate = useMemo(() => {
    if (!selectedDate || !availabilityEventDetails) return [];

    const selectedDateMoment = moment(selectedDate).tz(CAL_DOT_COM_TIME_ZONE, true).startOf('day');
    return availabilityEventDetails.filter((event) => {
      const eventStart = moment(event.start).tz(CAL_DOT_COM_TIME_ZONE);
      return eventStart.isSame(selectedDateMoment, 'day');
    });
  }, [selectedDate, availabilityEventDetails]);

  // Do not allow interactions with past calendar days
  const isPastCalendarDay = useCallback((dateStr) => {
    const day = moment.tz(dateStr, DateFormat.DEFAULT_DATE_FORMAT, CAL_DOT_COM_TIME_ZONE);
    const today = moment.tz(CAL_DOT_COM_TIME_ZONE).startOf('day');
    return day.isBefore(today, 'day');
  }, []);

  const openModalForDate = useCallback(async (dateStr) => {
    if (isPastCalendarDay(dateStr)) return;
    setSelectedDate(dateStr);
    setShowModal(true);
  }, [isPastCalendarDay]);

  const handleEventClick = useCallback(async (info) => {
    // Prevent the default behavior
    info.jsEvent.preventDefault();
    const eventDate = moment(info.event.start).format(DateFormat.DEFAULT_DATE_FORMAT);
    await openModalForDate(eventDate);
  }, [openModalForDate]);

  const renderEventContent = useCallback((eventInfo) => {
    const startTime = moment.tz(eventInfo.event.start, CAL_DOT_COM_TIME_ZONE).utc()
      .format(TimeDurationFormat.TWELVE_HOUR_FORMAT);
    const isMonthView = currentView === DAY_GRID_MONTH;
    return (
      <EventContent>
        {isMonthView && <EventTime>{startTime}</EventTime>}
        <EventTitle>{eventInfo.event.title}</EventTitle>
      </EventContent>
    );
  }, [currentView]);

  if (error) {
    return <EventContentError>{texts.error}</EventContentError>;
  }

  return (
    <>
      <CalendarWrapper>
        <TimeZoneText>
          {format(texts.timeZoneText, { timezone: CAL_DOT_COM_TIME_ZONE })}
        </TimeZoneText>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={DAY_GRID_MONTH}
          timeZone={CAL_DOT_COM_TIME_ZONE}
          fixedWeekCount={false}
          headerToolbar={{
            left: 'title',
            center: '',
            right: 'timeGridDay,timeGridWeek,dayGridMonth today prev,next',
          }}
          buttonText={{
            today: texts.calendarButtonText.today,
            month: texts.calendarButtonText.month,
            week: texts.calendarButtonText.week,
            day: texts.calendarButtonText.day,
          }}
          selectable
          selectMirror
          dayMaxEvents={2}
          moreLinkClick="popover"
          events={availabilityEventDetails}
          eventContent={renderEventContent}
          viewDidMount={(info) => setCurrentView(info.view.type)}
          dateClick={(info) => openModalForDate(info.dateStr)}
          eventClick={handleEventClick}
          height="auto"
          slotMinTime="00:00:00"
          slotMaxTime="23:59:00"
          allDaySlot={false}
          slotEventOverlap={false}
          businessHours={{
            daysOfWeek: [1, 2, 3, 4, 5, 6, 0], // All days
            startTime: '00:00',
            endTime: '23:59',
          }}
          eventDisplay="block"
          dayHeaderFormat={{ weekday: 'short' }}
          dayMaxEventRows={2}
          moreLinkContent={(arg) => {
            const count = arg.num;
            return format(texts.moreOptions, { amount: count });
          }}
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }}
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }}
          datesSet={(arg) => setVisibleDateRange(arg)}
        />
        <LoadingOverlay isLoading={isLoading} />
      </CalendarWrapper>
      <AvilabilityModal
        showModal={showModal}
        onClose={() => setShowModal(false)}
        selectedDateAvailabilityData={filteredEventsForSelectedDate}
        selectedDate={selectedDate}
        maxSeatsPerSlot={MAX_SEATS_PER_SLOT}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        setAvailabilityEventDetails={setAvailabilityEventDetails}
        setShowModal={setShowModal}
        visibleDateRange={visibleDateRange}
        setSelectedDate={setSelectedDate}
      />
    </>
  );
};

export default AvilabilityCalendar;
